// Shared fleet mapping utilities
import { v4 as uuidv4 } from 'uuid';
import { Fleet, Vehicle } from '../components/FleetMenu';
import { OperatingPeriod, BreakPeriod } from '../../../types/simulation';
import { createDefaultDateTime, combineDateWithTime, extractServiceDate } from '../../../utils/dateTimeUtils';
import { DEFAULT_SIMULATION_DATE } from '../../../config/constants';
import { registerShiftLabel, registerRouteMeta, encodeVehicleBrandLabel } from '../../../services/simulation/shiftLabelRegistry';

// Helper: Find point by ID and return { latitude, longitude }
export function getAddressById(id: string, allPoints: { id: string; name: string; coordinates?: [number, number] }[]): { latitude: number; longitude: number } {
  const point = allPoints.find(p => p.id === id);
  if (point && point.coordinates) {
    return { latitude: point.coordinates[0], longitude: point.coordinates[1] };
  }
  // Default coordinates if point not found
  return { latitude: 0, longitude: 0 };
}

// Helper: Resolve a vehicle's start/end address with depot-coordinate fallback.
// Slot-driven flows (Postcode Stop Assignment, ELC) set `startLocation` to a
// human-readable depot label (e.g. "Whitman Labs") which never
// matches a point ID — the lookup returns Null Island and the simulator emits
// `{0,0}` shift addresses. When that happens, fall back to the explicit
// `{lat, lng}` carried on the vehicle as `startLocationCoordinates` /
// `endLocationCoordinates`. Returns `{0,0}` only if neither source yields real
// coordinates.
function resolveVehicleAddress(
  locationId: string | undefined,
  locationCoordinates: { lat?: number; lng?: number } | undefined,
  allPoints: { id: string; name: string; coordinates?: [number, number] }[],
): { latitude: number; longitude: number } {
  // Prefer explicit depot coordinates when present. They are stronger evidence
  // than `locationId` because slot-driven flows set `locationId` to a
  // human-readable depot label (e.g. "Whitman Labs") that is NEVER
  // a real point id — and if such a label happened to collide with a point id,
  // the point lookup would silently win and pin the depot to the wrong place.
  if (
    locationCoordinates &&
    typeof locationCoordinates.lat === 'number' &&
    typeof locationCoordinates.lng === 'number' &&
    !(locationCoordinates.lat === 0 && locationCoordinates.lng === 0)
  ) {
    return { latitude: locationCoordinates.lat, longitude: locationCoordinates.lng };
  }
  if (typeof locationId === 'string' && locationId.trim() !== '') {
    const lookup = getAddressById(locationId, allPoints);
    if (!(lookup.latitude === 0 && lookup.longitude === 0)) {
      return lookup;
    }
  }
  return { latitude: 0, longitude: 0 };
}

// Helper: Get valid points from all map layers
export function getValidPoints(allPoints: any[]): { id: string; name: string; coordinates?: [number, number] }[] {
  return allPoints.filter(point => 
    point.id && 
    point.name && 
    point.coordinates && 
    Array.isArray(point.coordinates) && 
    point.coordinates.length === 2 &&
    typeof point.coordinates[0] === 'number' &&
    typeof point.coordinates[1] === 'number'
  );
}

// Helper: Convert time-only string to full canonical datetime string
// Output format matches the rides payload: "YYYY-MM-DDTHH:mm:ss.SSS" (no Z, no offset).
// Routing through combineDateWithTime keeps fleet times in lockstep with the rides
// path and avoids the "space-vs-T" parser asymmetry that has bitten downstream
// services in the past.
function convertTimeToFullDateTime(timeStr: string, serviceDate?: string): string {
  const baseDate = (() => {
    if (!serviceDate) return DEFAULT_SIMULATION_DATE;
    try {
      return extractServiceDate(serviceDate);
    } catch {
      return DEFAULT_SIMULATION_DATE;
    }
  })();

  // Already a full datetime (T or space separator) — re-normalise to canonical T-format
  // on the resolved base date so every shift/break in the payload shares one shape.
  const fullMatch = timeStr.match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/);
  if (fullMatch) {
    return combineDateWithTime(baseDate, `${fullMatch[1]}:${fullMatch[2]}`);
  }

  // Time-only HH:mm
  if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
    const [hh, mm] = timeStr.split(':');
    return combineDateWithTime(baseDate, `${hh.padStart(2, '0')}:${mm}`);
  }

  // Unknown shape — fall back to the original string to preserve old behaviour
  return timeStr;
}

/**
 * Check if a vehicle has on-demand service capability
 * (has at least one operating period with serviceType 'on-demand' or 'mixed')
 */
function hasOnDemandCapability(vehicle: Vehicle): boolean {
  if (!vehicle.operatingHours || vehicle.operatingHours.length === 0) {
    // Legacy vehicles without explicit periods should still be eligible
    return true;
  }

  return vehicle.operatingHours.some((period: OperatingPeriod) => {
    const serviceType = period.serviceType;
    return serviceType === 'on-demand' || serviceType === 'mixed' || serviceType === undefined;
  });
}

/**
 * Get only on-demand periods from a vehicle's operating hours
 * Filters out fixed-route-only periods
 */
function getOnDemandPeriods(vehicle: Vehicle): OperatingPeriod[] {
  return vehicle.operatingHours.filter((period: OperatingPeriod) => {
    const serviceType = period.serviceType;
    // Explicitly exclude fixed-route-only periods
    if (serviceType === 'fixed') {
      return false;
    }
    // Include periods that are on-demand or mixed
    // If serviceType is undefined, include it (backward compatibility - assume legacy fleets are on-demand)
    return serviceType === 'on-demand' || serviceType === 'mixed' || serviceType === undefined;
  });
}

// Map fleet to API fleet settings format
// For simulations: Only includes vehicles with on-demand capability and only their on-demand periods
export function mapFleetToApiFleetSettings(fleet: Fleet, allPoints: { id: string; name: string; coordinates?: [number, number] }[]): any {
  // Get serviceDate from fleet metadata (from imported fleet) or vehicle metadata
  const serviceDate = fleet.uatMetadata?.serviceDate || 
    (fleet.vehicles?.length > 0 ? fleet.vehicles[0]?.uatMetadata?.serviceDate : undefined);
  
  const result: any = {
    vehicles: fleet.vehicles
      // Filter to only include vehicles with on-demand capability
      .filter(vehicle => hasOnDemandCapability(vehicle))
      .map(vehicle => {
        // Get only on-demand periods for this vehicle
        const onDemandPeriods = getOnDemandPeriods(vehicle);
        
        // If no on-demand periods found, skip this vehicle (shouldn't happen due to filter above)
        if (onDemandPeriods.length === 0) {
          return null;
        }
        
        // Use vehicle-level serviceDate if available, otherwise use fleet-level
        const vehicleServiceDate = vehicle.uatMetadata?.serviceDate || serviceDate;
        
        // Capture display name so each shift can be registered in the
        // client-side label registry. Slot-driven flows (Postcode Stop
        // Assignment, ELC) populate `vehicle.name` to e.g. "DAY 1 COACH".
        const vehicleDisplayName: string | undefined =
          typeof vehicle.name === 'string' && vehicle.name.trim() !== ''
            ? vehicle.name
            : undefined;

        // Generate (or reuse) an `external_reference_id` for the vehicle and an
        // `external_driver_id` per shift. The simulator does NOT echo our
        // internal vehicle ids (slot.opsVehicleId / uatVehicleId etc.) back on
        // activities — it returns its own freshly-minted `vehicle_id` /
        // `vehicle_shift_id` UUIDs. By sending `external_reference_id` /
        // `external_driver_id` we get them echoed on `/results/{id}/vehicles`,
        // letting the results panel join shift labels back to activities
        // (see SimulationResultsMenu's activity-load join + shiftLabelRegistry).
        // Round 1 registered labels under client-only ids the API never saw —
        // that's why "Vehicle 1" still leaked through. (Ben 2026-05-28)
        const vehicleAnyRef = vehicle as unknown as {
          id?: string;
          uatVehicleId?: string;
          external_reference_id?: string;
          routeName?: string;
          shiftBand?: string;
          uatMetadata?: { originalId?: string };
        };

        // Route name / band for slot-driven flows (Postcode Stop Assignment,
        // ELC). Kept separate from `vehicleDisplayName` (the rego, which stays
        // on shift_name / vehicle_name) so the *route* the simulator builds is
        // labelled "DAY 1" while the physical vehicle is still matched by id.
        const vehicleRouteName =
          typeof vehicleAnyRef.routeName === 'string' && vehicleAnyRef.routeName.trim() !== ''
            ? vehicleAnyRef.routeName.trim()
            : undefined;
        const vehicleShiftBand =
          typeof vehicleAnyRef.shiftBand === 'string' && vehicleAnyRef.shiftBand.trim() !== ''
            ? vehicleAnyRef.shiftBand.trim()
            : undefined;
        const vehicleExternalReferenceId =
          (typeof vehicleAnyRef.external_reference_id === 'string' && vehicleAnyRef.external_reference_id.trim() !== ''
            ? vehicleAnyRef.external_reference_id
            : undefined) || uuidv4();

        return {
          number_of_vehicles: vehicle.numberOfVehicles || 1,
          vehicle: {
            capacity: [
              { type: "standard", number: vehicle.standardSeats },
              { type: "wheelchair", number: vehicle.wheelchairSeats }
            ],
            // Smuggle the vehicle label / route meta through `brand` (a field
            // HTTM persists + echoes on /vehicles but never reads) so the names
            // survive reopen / reload, unlike the in-memory registry below.
            brand: encodeVehicleBrandLabel({
              name: vehicleDisplayName,
              routeName: vehicleRouteName,
              shiftBand: vehicleShiftBand,
            }) ?? "Default",
            vehicle_type: "standard",
            external_reference_id: vehicleExternalReferenceId,
            shifts: onDemandPeriods.map((period: OperatingPeriod, shiftIndex: number) => {
              // Read depot coords off the vehicle. Slot-driven push-to-map
              // flows (Postcode Stop Assignment) populate these even though
              // `startLocation`/`endLocation` are human-readable labels rather
              // than point IDs.
              const startAddress = resolveVehicleAddress(
                vehicle.startLocation,
                vehicle.startLocationCoordinates,
                allPoints,
              );
              const endAddress = resolveVehicleAddress(
                vehicle.endLocation,
                vehicle.endLocationCoordinates,
                allPoints,
              );

              // Break locations must never default to (0, 0) — Null Island is
              // ~16,000km from any real depot, makes the shift infeasible, and
              // silently zeroes the solver output. Fall back to the shift's
              // start address whenever the break has no usable coordinate.
              const fallbackBreakAddress = {
                latitude: startAddress.latitude,
                longitude: startAddress.longitude,
              };
              const resolveBreakAddress = (raw: unknown): { latitude: number; longitude: number } => {
                if (typeof raw !== 'string' || raw.trim() === '') {
                  return fallbackBreakAddress;
                }
                // Accept either a point ID ("019e...") or a literal "lat,lng" string.
                if (raw.includes(',')) {
                  const [latStr, lngStr] = raw.split(',').map(s => s.trim());
                  const lat = Number(latStr);
                  const lng = Number(lngStr);
                  if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
                    return { latitude: lat, longitude: lng };
                  }
                  return fallbackBreakAddress;
                }
                const lookup = getAddressById(raw, allPoints);
                if (lookup.latitude === 0 && lookup.longitude === 0) {
                  return fallbackBreakAddress;
                }
                return lookup;
              };

              const shiftLabel = vehicleDisplayName
                ? onDemandPeriods.length > 1
                  ? `${vehicleDisplayName} #${shiftIndex + 1}`
                  : vehicleDisplayName
                : undefined;

              // Each shift gets its own `external_driver_id` so the simulator
              // can echo it back on `/results/{id}/vehicles` per shift.
              const externalDriverId = uuidv4();

              // Register under every plausible echo candidate. The two newly
              // generated keys (`vehicleExternalReferenceId`, `externalDriverId`)
              // are the load-bearing pair — they're now actually sent to the
              // simulator and will be echoed on `/results/{id}/vehicles`.
              // Internal ids stay registered as defensive fallbacks for any
              // legacy join site that still uses them. The registry de-dupes.
              const echoKeys = [
                vehicleExternalReferenceId,
                externalDriverId,
                vehicleAnyRef.id,
                vehicleAnyRef.uatVehicleId,
                vehicleAnyRef.external_reference_id,
                vehicleAnyRef.uatMetadata?.originalId,
              ];
              if (vehicleDisplayName && shiftLabel) {
                registerShiftLabel(echoKeys, shiftLabel);
              }
              // Route name / band travel the same join but stamp `route_name` /
              // `shift_band` (not the vehicle label) so the Fixed Route export
              // names the route "DAY 1" while the vehicle stays the rego.
              if (vehicleRouteName || vehicleShiftBand) {
                registerRouteMeta(echoKeys, { routeName: vehicleRouteName, shiftBand: vehicleShiftBand });
              }

              return {
                start_address: startAddress,
                end_address: endAddress,
                start_time: convertTimeToFullDateTime(period.start, vehicleServiceDate),
                end_time: convertTimeToFullDateTime(period.end, vehicleServiceDate),
                breaks: period.breaks?.map((b: BreakPeriod) => ({
                  duration_in_seconds: b.start && b.end ? Math.max(0, Math.floor((new Date(convertTimeToFullDateTime(b.end, vehicleServiceDate)).getTime() - new Date(convertTimeToFullDateTime(b.start, vehicleServiceDate)).getTime()) / 1000)) : 0,
                  start_time: b.start ? convertTimeToFullDateTime(b.start, vehicleServiceDate) : undefined,
                  end_time: b.end ? convertTimeToFullDateTime(b.end, vehicleServiceDate) : undefined,
                  break_addresses: [resolveBreakAddress(b.location)],
                })) || [],
                external_driver_id: externalDriverId,
                // Trim 3: send shift_name on the payload so the sim echoes it back; registry remains as a fallback (Ben 2026-05-28).
                ...(shiftLabel ? { shift_name: shiftLabel } : {}),
              };
            })
          }
        };
      })
      // Remove any null entries (shouldn't happen, but safety check)
      .filter((v): v is NonNullable<typeof v> => v !== null)
  };
  
  // Preserve fleet metadata in the transformed result so orchestrator can use it
  if (fleet.uatMetadata) {
    result.uatMetadata = fleet.uatMetadata;
  }
  
  return result;
} 